export type DynamicSettings = {
  wechatQrImage?: string;
  wechatChannelsUrl?: string;
  wechatChannelsQrImage?: string;
  douyinUrl?: string;
  douyinQrImage?: string;
  bilibiliUrl?: string;
  bilibiliQrImage?: string;
  xiaohongshuUrl?: string;
  xiaohongshuQrImage?: string;
  weiboUrl?: string;
  weiboQrImage?: string;
  facebookUrl?: string;
  facebookQrImage?: string;
  instagramUrl?: string;
  instagramQrImage?: string;
  linkedinUrl?: string;
  linkedinQrImage?: string;
  twitterUrl?: string;
  twitterQrImage?: string;
};

type SocialItem = {
  key: string;
  label: string;
  href?: string;
  qr?: string;
};

export function renderSocialLinksHtml(settings: DynamicSettings = {}, lang: "zh" | "en" = "zh") {
  const items: SocialItem[] = [
    { key: "wechat", label: lang === "en" ? "WeChat Official Account" : "微信公众号", qr: settings.wechatQrImage },
    { key: "channels", label: lang === "en" ? "WeChat Channels" : "视频号", href: settings.wechatChannelsUrl, qr: settings.wechatChannelsQrImage },
    { key: "douyin", label: lang === "en" ? "Douyin" : "抖音", href: settings.douyinUrl, qr: settings.douyinQrImage },
    { key: "xiaohongshu", label: lang === "en" ? "Xiaohongshu" : "小红书", href: settings.xiaohongshuUrl, qr: settings.xiaohongshuQrImage },
    { key: "weibo", label: lang === "en" ? "Weibo" : "微博", href: settings.weiboUrl, qr: settings.weiboQrImage },
    { key: "bilibili", label: "Bilibili", href: settings.bilibiliUrl, qr: settings.bilibiliQrImage },
    { key: "facebook", label: "Facebook", href: settings.facebookUrl, qr: settings.facebookQrImage },
    { key: "instagram", label: "Instagram", href: settings.instagramUrl, qr: settings.instagramQrImage },
    { key: "linkedin", label: "LinkedIn", href: settings.linkedinUrl, qr: settings.linkedinQrImage },
    { key: "x", label: "X", href: settings.twitterUrl, qr: settings.twitterQrImage },
  ];

  return `<div class="rt-social-links" aria-label="${escapeAttribute(lang === "en" ? "Social media" : "社交媒体")}">${items
    .map((item) => renderSocialItem(item))
    .join("")}</div>`;
}

function renderSocialItem(item: SocialItem) {
  const href = item.href && isSafeLinkUrl(item.href) ? item.href : "";
  const qr = item.qr && isSafeImageSrc(item.qr)
    ? `<img src="${escapeAttribute(item.qr)}" alt="${escapeAttribute(item.label)}" loading="lazy" />`
    : "";
  const content = `<span class="rt-social-icon rt-social-icon-${escapeAttribute(item.key)}" title="${escapeAttribute(item.label)}" aria-label="${escapeAttribute(item.label)}">${socialIconSvg(item.key)}</span>${qr}`;

  if (href) {
    return `<a href="${escapeAttribute(href)}" target="_blank" rel="noreferrer" aria-label="${escapeAttribute(item.label)}">${content}</a>`;
  }
  return `<span class="rt-social-link-placeholder" aria-label="${escapeAttribute(item.label)}">${content}</span>`;
}

function socialIconSvg(key: string) {
  if (key === "wechat") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.2 6.1c-3.4 0-6.2 2.1-6.2 4.8 0 1.5.9 2.9 2.3 3.8l-.5 1.8 2.2-1c.7.2 1.4.3 2.2.3.3 0 .6 0 .9-.1a5 5 0 0 1-.2-1.3c0-2.8 2.7-5.1 6.1-5.3-.7-2.2-3.4-3.8-6.8-3.8Zm-2.1 3.5a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6Zm4.2 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6Z"/><path d="M15.9 10.9c-2.9 0-5.2 1.8-5.2 4 0 2.3 2.3 4.1 5.2 4.1.6 0 1.3-.1 1.8-.3l1.8.8-.4-1.5c1.2-.8 2-1.9 2-3.1 0-2.2-2.4-4-5.2-4Zm-1.8 2.6a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4Zm3.6 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4Z"/></svg>`;
  }
  if (key === "channels") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5.5h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3Zm3.3 3.9v5.2l5-2.6-5-2.6Z"/></svg>`;
  }
  if (key === "douyin") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h3c.3 2 1.4 3.1 3 3.5v3.1a7.4 7.4 0 0 1-3.8-1.4v5.4A5.4 5.4 0 1 1 10.8 9v3.2a2.3 2.3 0 1 0 2.3 2.3V4H14Z"/></svg>`;
  }
  if (key === "instagram") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4Zm4 4.3a3.7 3.7 0 1 0 0 7.4 3.7 3.7 0 0 0 0-7.4Zm5-1.1a1 1 0 1 0 0 2.1 1 1 0 0 0 0-2.1Z"/></svg>`;
  }
  if (key === "linkedin") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h3.3v10H5V9Zm1.7-4a1.9 1.9 0 1 1 0 3.8 1.9 1.9 0 0 1 0-3.8ZM10 9h3.2v1.4c.5-.8 1.5-1.7 3.2-1.7 3.4 0 4 2.2 4 5V19h-3.3v-4.7c0-1.1 0-2.5-1.5-2.5s-1.8 1.2-1.8 2.5V19H10V9Z"/></svg>`;
  if (key === "facebook") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 8h2.2V4.4c-.4 0-1.7-.1-3.2-.1-3.2 0-5.3 1.9-5.3 5.5V13H4.4v4h3.3v7h4.1v-7h3.2l.5-4h-3.7V10c0-1.2.3-2 2.2-2Z"/></svg>`;
  if (key === "x") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h4.3l3.1 4.4L16.4 4H20l-5.8 6.5L21 20h-4.3l-3.7-5.2L8.4 20H4.8l6.4-7.2L5 4Zm2.6 1.8 10 12.4h1L8.6 5.8h-1Z"/></svg>`;
  if (key === "bilibili") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8.1 4 2.2 2.5h3.4L15.9 4l1.5 1.3-1.1 1.2H18a3 3 0 0 1 3 3v6.8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9.5a3 3 0 0 1 3-3h1.7L6.6 5.3 8.1 4Zm-2 5.4v6.7h11.8V9.4H6.1Zm2.5 2h2v2h-2v-2Zm4.8 0h2v2h-2v-2Z"/></svg>`;
  if (key === "xiaohongshu") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h12a2 2 0 0 1 2 2v12.5a1.5 1.5 0 0 1-2.2 1.3L12 16.7l-5.8 3.1A1.5 1.5 0 0 1 4 18.5V6a2 2 0 0 1 2-2Zm2 5h8V7H8v2Zm0 4h8v-2H8v2Z"/></svg>`;
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 15.5c3.9 1.1 7.9-.1 9.2-2.7 1-2-.3-4.2-3-5.1-3.5-1.2-7.3 0-8.6 2.5-1.1 2 .1 4.3 2.4 5.3Zm1.8-3.7a2.8 2.1-16 1 0 5.4-1.6 2.8 2.1-16 0 0-5.4 1.6Z"/><circle cx="17" cy="6.5" r="2"/><circle cx="19.5" cy="9.5" r="1.4"/></svg>`;
}

function isSafeLinkUrl(value: string) {
  return /^(https?:\/\/|\/|#)/i.test(value);
}

function isSafeImageSrc(value: string) {
  return /^(https?:\/\/|\/|data:image\/(?:png|jpeg|jpg|webp|gif);base64,)/i.test(value);
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll("'", "&#39;");
}
